<template>
  <div class="orangehrm-background-container year-end-report">
    <section class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title"
        >Year-End Leave Report</oxd-text
      >
      <oxd-divider />
      <oxd-text tag="p" class="report-description">
        Review leave entitlements, usage and available balances for all active
        staff. Recipient selection controls who receives the consolidated
        report; it does not change which staff are included.
      </oxd-text>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="selectedYear"
            type="select"
            label="Report year"
            :options="yearOptions"
            :disabled="loadingOptions || generating || sendLocked"
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="includeProjection"
            type="switch"
            label="Include next-January projection"
            :disabled="generating || sendLocked"
          />
        </oxd-grid-item>
      </oxd-grid>
      <div class="report-actions">
        <oxd-button
          type="button"
          display-type="secondary"
          :label="generating ? 'Generating preview…' : 'Generate Preview'"
          :disabled="
            !selectedYear || loadingOptions || generating || sendLocked
          "
          @click="generatePreview"
        />
        <oxd-button
          v-if="preview"
          type="button"
          display-type="ghost"
          label="Download complete CSV"
          @click="downloadCsv"
        />
      </div>
      <oxd-text v-if="error" tag="p" class="report-error" role="alert">
        {{ error }}
      </oxd-text>
      <oxd-text v-if="!preview" tag="p" class="report-description">
        Generate a preview to review the figures. Previewing and downloading do
        not send email or change leave records.
      </oxd-text>
    </section>

    <section v-if="preview" class="orangehrm-card-container report-section">
      <oxd-text tag="h6">{{ preview.report.year }} leave summary</oxd-text>
      <oxd-text tag="p" class="report-description">
        {{ preview.report.employeeCount }} active staff ·
        {{ preview.report.periodStart }} to {{ preview.report.periodEnd }} · As
        of
        {{ preview.report.asOfDate }}
      </oxd-text>
      <oxd-text tag="p" class="report-description">
        Generated {{ preview.report.generatedAt }} ({{
          preview.report.timezone
        }})
      </oxd-text>
      <oxd-text tag="p" class="report-description">
        {{ preview.report.basis }}
      </oxd-text>
      <ul v-if="preview.report.warnings.length" class="report-warnings">
        <li v-for="(warning, index) in preview.report.warnings" :key="index">
          {{ warning }}
        </li>
      </ul>
      <div class="report-table-scroll" tabindex="0" aria-label="Leave summary">
        <table class="report-table">
          <caption>
            Usage is shown in days and recorded hours. Available hours use the
            current work shift. Past approved leave is separate from recorded
            taken leave.
          </caption>
          <thead>
            <tr>
              <th scope="col">Staff member</th>
              <th scope="col">Leave type</th>
              <th scope="col">Entitlement (days)</th>
              <th scope="col">Taken</th>
              <th scope="col">Scheduled</th>
              <th scope="col">Pending</th>
              <th scope="col">Past approved</th>
              <th scope="col">Available</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in visibleRows" :key="rowKey(row)">
              <tr>
                <th scope="row">{{ row.employeeName }}</th>
                <td>
                  {{ row.leaveTypeName
                  }}{{ row.leaveTypeDeleted ? ' (archived)' : '' }}
                </td>
                <td>{{ amount(row.entitledDays) }}</td>
                <td>{{ duration(row.takenDays, row.takenHours) }}</td>
                <td>{{ duration(row.scheduledDays, row.scheduledHours) }}</td>
                <td>{{ duration(row.pendingDays, row.pendingHours) }}</td>
                <td>
                  {{ duration(row.pastApprovedDays, row.pastApprovedHours) }}
                </td>
                <td>{{ duration(row.availableDays, row.availableHours) }}</td>
              </tr>
              <tr v-if="row.warnings.length" class="report-row-warning">
                <td colspan="8">
                  <strong
                    >{{ row.employeeName }} — {{ row.leaveTypeName }}:</strong
                  >
                  {{ row.warnings.join(' ') }}
                </td>
              </tr>
            </template>
            <tr v-if="!preview.report.rows.length">
              <td colspan="8">No report rows are available.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <template v-if="preview.report.includeProjection">
        <oxd-text tag="h6" class="report-section-title">
          Provisional opening balances — {{ preview.report.projectionDate }}
        </oxd-text>
        <oxd-text tag="p" class="report-description">
          These are projected opening grants and carryover, subject to
          eligibility, policy and record changes. They are not guaranteed
          final-year earnings. Generating this report does not post next year's
          balances.
        </oxd-text>
        <div
          class="report-table-scroll"
          tabindex="0"
          aria-label="Provisional opening balances"
        >
          <table class="report-table">
            <caption>
              Projected days and hours for the same staff and leave types shown
              above. Unavailable figures require review.
            </caption>
            <thead>
              <tr>
                <th scope="col">Staff member</th>
                <th scope="col">Leave type</th>
                <th scope="col">Opening grant (days)</th>
                <th scope="col">Carryover (days)</th>
                <th scope="col">Opening total</th>
                <th scope="col">Review</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in visibleRows" :key="rowKey(row)">
                <th scope="row">{{ row.employeeName }}</th>
                <td>{{ row.leaveTypeName }}</td>
                <td>{{ amount(row.projection?.grantedDays) }}</td>
                <td>{{ amount(row.projection?.carryoverDays) }}</td>
                <td>
                  {{
                    duration(
                      row.projection?.totalDays,
                      row.projection?.totalHours,
                    )
                  }}
                </td>
                <td>
                  {{ projectionStatus(row.projection) }}
                  {{ row.projection?.warnings.join(' ') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <div
        v-if="preview.report.rows.length"
        class="report-actions report-pagination"
      >
        <oxd-button
          type="button"
          display-type="ghost"
          label="Previous"
          :disabled="page === 1"
          @click="page--"
        />
        <oxd-text tag="p" aria-live="polite">
          Rows {{ (page - 1) * pageSize + 1 }}–{{
            Math.min(page * pageSize, preview.report.rows.length)
          }}
          of {{ preview.report.rows.length }} · Page {{ page }} of
          {{ pageCount }}
        </oxd-text>
        <oxd-button
          type="button"
          display-type="ghost"
          label="Next"
          :disabled="page >= pageCount"
          @click="page++"
        />
      </div>
    </section>

    <section class="orangehrm-card-container report-section">
      <oxd-text tag="h6">Email this report</oxd-text>
      <oxd-text tag="p" class="report-description">
        Each selected recipient receives the complete active-staff report as a
        CSV attachment, including any provisional projection. Choose recipients
        carefully; the report contains staff leave balances.
      </oxd-text>
      <oxd-text
        v-if="!loadingOptions && !mailConfigured"
        tag="p"
        class="report-warning"
      >
        Email is not configured. You can still preview and download the report.
      </oxd-text>
      <oxd-grid :cols="2" class="orangehrm-full-width-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="selectedRecipients"
            type="multiselect"
            label="Staff work-email recipients"
            :options="recipientOptions"
            :disabled="loadingOptions || sendLocked"
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="additionalRecipients"
            type="textarea"
            label="Additional email addresses"
            placeholder="Separate addresses with commas or new lines"
            :disabled="sendLocked"
          />
        </oxd-grid-item>
      </oxd-grid>
      <oxd-text v-if="recipientError" tag="p" class="report-error" role="alert">
        {{ recipientError }}
      </oxd-text>
      <oxd-text tag="p" class="report-description">
        {{ recipientEmails.length }} selected (maximum 50). No recipients are
        selected automatically.
      </oxd-text>
      <ul v-if="recipientEmails.length" class="report-addresses">
        <li v-for="email in recipientEmails" :key="email">{{ email }}</li>
      </ul>
      <oxd-button
        type="button"
        display-type="secondary"
        label="Review recipients"
        :disabled="!canReview"
        @click="openConfirmation"
      />

      <section
        v-if="confirmOpen && preview"
        class="report-confirmation"
        aria-labelledby="report-confirmation-title"
      >
        <oxd-text id="report-confirmation-title" tag="h6"
          >Confirm report email</oxd-text
        >
        <oxd-text tag="p" class="report-description">
          Send the complete {{ preview.report.year }} report for
          {{ preview.report.employeeCount }} active staff to these
          {{ reviewedRecipients.length }} recipients. Each address receives a
          separate email.
        </oxd-text>
        <ul class="report-addresses">
          <li v-for="email in reviewedRecipients" :key="email">{{ email }}</li>
        </ul>
        <div class="report-actions">
          <oxd-button
            type="button"
            display-type="ghost"
            label="Cancel"
            :disabled="sending"
            @click="confirmOpen = false"
          />
          <oxd-button
            type="button"
            display-type="secondary"
            label="Confirm and send report"
            :disabled="!canReview || sending"
            @click="confirmSend"
          />
        </div>
      </section>
      <oxd-text v-if="sending" tag="p" class="report-description" role="status">
        Checking the report and sending. Keep this page open until the result is
        confirmed.
      </oxd-text>
      <div v-if="uncertainSend" class="report-warning" role="status">
        <oxd-text tag="p">
          The send outcome is not yet confirmed. Check send history and the
          recipients' mail before generating another send. Checking this request
          again reuses the original request and cannot send it twice.
        </oxd-text>
        <oxd-button
          type="button"
          display-type="ghost"
          label="Check this send request"
          :disabled="sending"
          @click="sendCurrentAttempt"
        />
      </div>
      <div v-if="receipt" class="report-receipt" role="status">
        <oxd-text tag="h6">{{ deliveryStatus(receipt.status) }}</oxd-text>
        <oxd-text tag="p">{{ receipt.message }}</oxd-text>
        <ul class="report-addresses">
          <li v-for="recipient in receipt.recipients" :key="recipient.email">
            {{ recipient.email }} — {{ deliveryStatus(recipient.status) }}
          </li>
        </ul>
      </div>
    </section>

    <section class="orangehrm-card-container report-section">
      <div class="report-actions">
        <oxd-text tag="h6">Recent send history</oxd-text>
        <oxd-button
          type="button"
          display-type="ghost"
          label="Refresh history"
          :disabled="loadingHistory"
          @click="loadHistory"
        />
      </div>
      <oxd-text tag="p" class="report-description">
        Sent means the mail provider accepted the message; it does not confirm
        inbox delivery. Failed or partial results are not retried automatically.
      </oxd-text>
      <oxd-text v-if="historyError" tag="p" class="report-error" role="alert">
        {{ historyError }}
      </oxd-text>
      <oxd-text v-if="!loadingHistory && !deliveries.length" tag="p">
        No report sends are recorded.
      </oxd-text>
      <article
        v-for="delivery in deliveries"
        :key="delivery.id"
        class="report-history-entry"
      >
        <oxd-text tag="p">
          {{ delivery.createdAt }} · {{ delivery.year }} report ·
          {{ deliveryStatus(delivery.status) }}
        </oxd-text>
        <oxd-text tag="p">{{ delivery.message }}</oxd-text>
        <ul class="report-addresses">
          <li v-for="recipient in delivery.recipients" :key="recipient.email">
            {{ recipient.email }} — {{ deliveryStatus(recipient.status) }}
          </li>
        </ul>
      </article>
    </section>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';

const reportPath = '/api/v2/leave/year-end-report';

export default {
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, reportPath);
    http.setIgnorePath(reportPath);
    return {http};
  },
  data() {
    return {
      loadingOptions: true,
      loadingHistory: false,
      generating: false,
      sending: false,
      currentYear: null,
      selectedYear: null,
      includeProjection: true,
      recipientOptions: [],
      selectedRecipients: [],
      additionalRecipients: '',
      mailConfigured: false,
      preview: null,
      previewFilters: null,
      previewVersion: 0,
      page: 1,
      pageSize: 25,
      confirmOpen: false,
      reviewedRecipients: [],
      sendAttempt: null,
      uncertainSend: false,
      receipt: null,
      deliveries: [],
      error: '',
      historyError: '',
    };
  },
  computed: {
    yearOptions() {
      return this.currentYear
        ? Array.from({length: this.currentYear - 1999}, (_, index) => ({
            id: this.currentYear - index,
            label: String(this.currentYear - index),
          }))
        : [];
    },
    recipientEmails() {
      return [
        ...new Set(
          [
            ...this.selectedRecipients.map((recipient) => recipient.email),
            ...this.additionalRecipients.split(/[,;\n\r]+/),
          ]
            .map((email) => (email || '').trim().toLowerCase())
            .filter(Boolean),
        ),
      ].sort();
    },
    recipientError() {
      if (this.recipientEmails.length > 50)
        return 'Select no more than 50 recipients.';
      if (
        this.recipientEmails.some(
          (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
        )
      ) {
        return 'Check the email addresses. Each must contain a valid name and domain.';
      }
      return '';
    },
    sendLocked() {
      return this.sending || this.uncertainSend;
    },
    canReview() {
      return Boolean(
        this.preview &&
          this.previewFilters?.year === this.selectedYear?.id &&
          this.previewFilters?.includeProjection === this.includeProjection &&
          !this.generating &&
          !this.loadingOptions &&
          !this.sendLocked &&
          !this.sendAttempt &&
          this.mailConfigured &&
          this.recipientEmails.length &&
          !this.recipientError,
      );
    },
    visibleRows() {
      const start = (this.page - 1) * this.pageSize;
      return (
        this.preview?.report.rows.slice(start, start + this.pageSize) || []
      );
    },
    pageCount() {
      return Math.max(
        1,
        Math.ceil((this.preview?.report.rows.length || 0) / this.pageSize),
      );
    },
  },
  watch: {
    selectedYear() {
      this.invalidatePreview();
    },
    includeProjection() {
      this.invalidatePreview();
    },
    recipientEmails() {
      this.confirmOpen = false;
    },
  },
  beforeMount() {
    this.loadOptions();
    this.loadHistory();
  },
  methods: {
    errorMessage(error, fallback) {
      const message =
        error?.response?.data?.error?.message || error?.data?.error?.message;
      return typeof message === 'string' && message.trim() ? message : fallback;
    },
    async loadOptions() {
      try {
        const {data} = await this.http.getAll({view: 'options'});
        this.currentYear = data.data.currentYear;
        this.selectedYear = {
          id: data.data.defaultYear,
          label: String(data.data.defaultYear),
        };
        this.recipientOptions = data.data.recipients.map((recipient) => ({
          id: recipient.empNumber,
          label: `${recipient.label} — ${recipient.email}`,
          email: recipient.email,
        }));
        this.mailConfigured = data.data.mailConfigured;
      } catch (error) {
        this.error = this.errorMessage(
          error,
          'Unable to load report options. Reload the page to try again.',
        );
      } finally {
        this.loadingOptions = false;
      }
    },
    async loadHistory() {
      if (this.loadingHistory) return;
      this.loadingHistory = true;
      this.historyError = '';
      try {
        const {data} = await this.http.getAll({view: 'history'});
        this.deliveries = data.data.deliveries;
        const current = this.deliveries.find(
          (delivery) => delivery.id === this.receipt?.id,
        );
        if (current && this.uncertainSend) {
          this.receipt = current;
          this.uncertainSend = current.status === 'sending';
        }
      } catch (error) {
        this.historyError = this.errorMessage(
          error,
          'Unable to load send history.',
        );
      } finally {
        this.loadingHistory = false;
      }
    },
    invalidatePreview() {
      this.previewVersion++;
      this.preview = null;
      this.previewFilters = null;
      this.confirmOpen = false;
      if (!this.sendLocked) this.sendAttempt = null;
    },
    async generatePreview() {
      if (
        !this.selectedYear ||
        this.loadingOptions ||
        this.generating ||
        this.sendLocked
      )
        return;
      this.invalidatePreview();
      const version = this.previewVersion;
      const filters = {
        year: this.selectedYear.id,
        includeProjection: this.includeProjection,
      };
      this.generating = true;
      this.receipt = null;
      this.error = '';
      try {
        const {data} = await this.http.getAll({view: 'preview', ...filters});
        if (version !== this.previewVersion) return;
        this.preview = data.data;
        this.previewFilters = filters;
        this.page = 1;
      } catch (error) {
        if (version === this.previewVersion) {
          this.error = this.errorMessage(
            error,
            'Unable to generate the report preview.',
          );
        }
      } finally {
        this.generating = false;
      }
    },
    downloadCsv() {
      if (!this.preview) return;
      const url = URL.createObjectURL(
        new Blob([this.preview.csv.content], {type: 'text/csv;charset=utf-8;'}),
      );
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = this.preview.csv.filename;
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
        URL.revokeObjectURL(url);
      }
    },
    openConfirmation() {
      if (!this.canReview) return;
      this.reviewedRecipients = [...this.recipientEmails];
      this.confirmOpen = true;
    },
    async confirmSend() {
      if (!this.confirmOpen || !this.canReview) return;
      if (
        JSON.stringify(this.reviewedRecipients) !==
        JSON.stringify(this.recipientEmails)
      ) {
        this.confirmOpen = false;
        return;
      }
      this.sendAttempt = {
        ...this.previewFilters,
        reportHash: this.preview.reportHash,
        requestKey: this.preview.requestKey,
        recipientEmails: [...this.reviewedRecipients],
        confirmed: true,
      };
      this.confirmOpen = false;
      await this.sendCurrentAttempt();
    },
    async sendCurrentAttempt() {
      if (
        this.sending ||
        !this.sendAttempt ||
        (this.receipt && !this.uncertainSend)
      )
        return;
      const wasUncertain = this.uncertainSend;
      this.sending = true;
      this.error = '';
      try {
        const {data} = await this.http.create(this.sendAttempt);
        this.receipt = data.data;
        this.uncertainSend = this.receipt.status === 'sending';
        await this.loadHistory();
      } catch (error) {
        const status = Number(error?.response?.status || error?.status);
        if ([400, 422].includes(status) && !wasUncertain) {
          this.uncertainSend = false;
          this.sendAttempt = null;
          this.invalidatePreview();
          this.error = this.errorMessage(
            error,
            'The report could not be sent. Generate a fresh preview and review the recipients again.',
          );
        } else {
          // Keep the exact original key and payload after an uncertain response.
          this.uncertainSend = true;
          this.error =
            'The send result could not be confirmed. Check this same request again before starting a new send.';
        }
      } finally {
        this.sending = false;
      }
    },
    rowKey(row) {
      return `${row.empNumber}-${row.leaveTypeId}`;
    },
    amount(value) {
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        !Number.isFinite(Number(value))
      )
        return 'Unavailable';
      return Number(value).toLocaleString(undefined, {
        maximumFractionDigits: 4,
      });
    },
    duration(days, hours) {
      const dayValue = this.amount(days);
      const hourValue = this.amount(hours);
      return `${
        dayValue === 'Unavailable' ? 'Days unavailable' : `${dayValue} days`
      } / ${
        hourValue === 'Unavailable' ? 'hours unavailable' : `${hourValue} h`
      }`;
    },
    projectionStatus(projection) {
      return projection ? projection.status.replace(/_/g, ' ') : 'Unavailable';
    },
    deliveryStatus(status) {
      return (
        {
          sent: 'Accepted by mail provider',
          partial: 'Partially accepted',
          failed: 'Failed',
          sending: 'Sending — outcome not confirmed',
          pending: 'Not yet sent',
        }[status] || status
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.year-end-report {
  .report-section {
    margin-top: 1rem;
  }
  .report-description {
    margin: 1rem 0;
    line-height: 1.6;
  }
  .report-section-title {
    margin-top: 2rem;
  }
  .report-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }
  .report-pagination {
    justify-content: center;
    margin-top: 1rem;
  }
  .report-table-scroll {
    overflow-x: auto;
  }
  .report-table {
    width: 100%;
    border-collapse: collapse;
    font: inherit;
    font-size: 0.85rem;
    th,
    td {
      padding: 0.75rem;
      text-align: left;
      vertical-align: top;
      border-bottom: 1px solid #e5e5e5;
    }
    th {
      font-weight: 600;
    }
    caption {
      text-align: left;
      padding: 0.75rem 0;
      line-height: 1.5;
    }
    thead {
      background: $oxd-background-pastel-white-color;
    }
  }
  .report-addresses,
  .report-warnings {
    padding-left: 1.5rem;
    font-size: 0.9rem;
    line-height: 1.7;
    overflow-wrap: anywhere;
  }
  .report-error {
    padding: 1rem;
    margin: 1rem 0;
    border-left: 3px solid $oxd-feedback-danger-color;
  }
  .report-warning,
  .report-confirmation,
  .report-receipt {
    margin-top: 1rem;
    padding: 1rem;
    background: $oxd-background-pastel-white-color;
  }
  .report-confirmation {
    border: 1px solid #b7b7b7;
    border-radius: 0.5rem;
  }
  .report-history-entry {
    padding: 1rem 0;
    border-top: 1px solid #e5e5e5;
  }
  .report-row-warning {
    font-size: 0.8rem;
  }
}
</style>
